import { useMemo, useRef } from 'react';
import { useSortBy, useTable } from 'react-table';

import clsx from 'clsx';
import dayjs from 'dayjs';

import useFormatNumber from '@/hooks/format/useFormatNumber';
//hooks
import useUser from '@/utils/hooks/swr/useUser';
import useTranslation from '@/utils/hooks/translation/useTranslation';

//utils
import useMarketingUrl from '@/utils/urls/useMarketingUrl';

import PropTypes from 'prop-types';

import Button from '@/components/base/Button';
// Components
import Icon from '@/components/base/Icon';
import InfoBanner from '@/components/base/InfoBanner';
import Modal from '@/components/base/Modal';
import { Trans } from '@/components/tools/translation';

const switchToPaddle = dayjs('2020-06-01').format('YYYY-MM-DD');

function Table({ columns, data }) {
    const { getTableProps, getTableBodyProps, headerGroups, rows, prepareRow } = useTable(
        {
            columns,
            data,
            autoResetSortBy: false
        },
        useSortBy
    );

    return (
        <table className="mb-8 w-full text-left" {...getTableProps()}>
            <thead>
                {headerGroups.map((headerGroup, i) => (
                    <tr key={i} {...headerGroup.getHeaderGroupProps()}>
                        {headerGroup.headers.map((column) => (
                            <th
                                key={column.id}
                                {...column.getHeaderProps(
                                    column.id === 'created' && column.getSortByToggleProps()
                                )}
                                className="px-5 py-4 font-GSemiBold text-xs text-icoBlack dark:text-white">
                                {column.render('Header')}
                            </th>
                        ))}
                    </tr>
                ))}
            </thead>
            <tbody {...getTableBodyProps()}>
                {rows.map((row, i) => {
                    prepareRow(row);
                    return (
                        <tr
                            key={i}
                            {...row.getRowProps()}
                            className="odd:bg-icoGray-100 odd:dark:bg-icoDarkMode-deepBlack">
                            {row.cells.map((cell) => (
                                <td
                                    key={cell.row.id}
                                    {...cell.getCellProps()}
                                    className="px-5 py-4 text-sm">
                                    {cell.render('Cell')}
                                </td>
                            ))}
                        </tr>
                    );
                })}
            </tbody>
        </table>
    );
}

export const InvoicesListModal = ({
    isOpen,
    toggle,
    invoices,
    formatDate,
    canLoadMore,
    isLoadingMore,
    loadMoreReceipts
}) => {
    const { t } = useTranslation('settings');
    const { user } = useUser();
    const { marketingUrl } = useMarketingUrl();
    const initRef = useRef(null);
    const userNumberFormat = user?.settings?.numbers_format ?? 'comma';
    const { formatPriceCurrencySymbol } = useFormatNumber(userNumberFormat);

    const userFormattedCreationDate = dayjs(user?.created_at)?.format('YYYY-MM-DD');

    const columns = useMemo(
        () => [
            {
                Header: () => (
                    <span className="xl:pl-8">{t('subscription_invoices_page.invoice_id')}</span>
                ),
                accessor: 'id',
                Cell: ({ value }) => <span className="xl:pl-8">{value}</span>
            },
            {
                Header: t('subscription_invoices_page.subscription'),
                accessor: 'subscription',
                Cell: ({ value }) => <span className="font-GSemiBold">{value}</span>
            },
            {
                Header: t('subscription_invoices_page.total_invoice'),
                accessor: 'total',
                Cell: ({ value, row }) => (
                    <span className="font-GSemiBold">
                        {String(formatPriceCurrencySymbol(value, row?.original?.currency))}
                    </span>
                )
            },
            {
                Header: ({ column }) => (
                    <p className="flex items-center text-icoBlue">
                        {column.isSorted ? (
                            <Icon
                                icon="chevron-bottom"
                                className={clsx('mr-2 transition', {
                                    'rotate-180 transform': !column.isSortedDesc
                                })}
                            />
                        ) : (
                            ''
                        )}
                        {t('subscription_invoices_page.created')}
                    </p>
                ),
                accessor: 'created',
                Cell: ({ value }) => <p className="w-full">{formatDate(value)}</p>
            },
            {
                Header: () => (
                    <p className="text-center">{t('subscription_invoices_page.download')}</p>
                ),
                accessor: 'url',
                Cell: ({ value }) => (
                    <div className="flex w-full items-center justify-center gap-5">
                        <a
                            href={value}
                            title={t('commons:download')}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="transition hover:scale-125">
                            <Icon
                                className="text-sm text-icoGray-500 transition hover:scale-125"
                                icon="export"
                            />
                        </a>
                    </div>
                )
            }
        ],

        [formatDate, t]
    );

    const data = useMemo(() => invoices, [invoices]);

    return (
        <Modal size="full" open={isOpen} initialFocus={initRef} onDismiss={toggle}>
            <Modal.Body className="min-h-full w-full bg-white px-10 py-10 dark:bg-icoDarkMode-anthracite md:px-20 md:py-20 xl:px-40">
                <Modal.Title className="mb-8 text-left font-GBold">
                    {t('subscription_invoices_page.invoices')}
                </Modal.Title>
                {dayjs(userFormattedCreationDate).isBefore(dayjs(switchToPaddle)) ? (
                    <InfoBanner
                        type="info"
                        title={t('commons:infos')}
                        message={
                            <Trans
                                i18nKey="settings:subscription_invoices_page.paddle_notice"
                                components={{
                                    a: (
                                        // eslint-disable-next-line jsx-a11y/anchor-has-content
                                        <a
                                            key="contact"
                                            target="_blank"
                                            href={marketingUrl('contact')}
                                            className="underline"
                                            rel="noopener noreferrer"
                                        />
                                    )
                                }}
                            />
                        }
                        className="w-full"
                    />
                ) : null}
                <Modal.Content className="pt-m text-center font-GMedium text-icoGray-500">
                    <Table columns={columns} data={data} />
                    {canLoadMore ? (
                        <Button isLoading={isLoadingMore} onClick={loadMoreReceipts}>
                            {t('subscription_invoices_page.load_more')}
                        </Button>
                    ) : null}
                </Modal.Content>
            </Modal.Body>
        </Modal>
    );
};

InvoicesListModal.propTypes = {
    isOpen: PropTypes.bool.isRequired,
    toggle: PropTypes.func.isRequired,
    invoices: PropTypes.array.isRequired,
    formatDate: PropTypes.func.isRequired,
    canLoadMore: PropTypes.bool.isRequired,
    isLoadingMore: PropTypes.bool.isRequired,
    loadMoreReceipts: PropTypes.func.isRequired
};

export default InvoicesListModal;
